import { useState, useEffect } from 'react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, Trash2, ArrowRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const { items: cartItems, addToCart, removeFromCart, updateQuantity, total, clearCart } = useCart();
  const { user, token } = useAuth();
  const [showCart, setShowCart] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/shop/items`);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (item) => {
    addToCart(item);
    toast.success(`${item.name} added to cart`);
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Please login to checkout');
      navigate('/login');
      return;
    }

    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setCheckoutLoading(true);

    try {
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        {
          enrollment_type: 'shop',
          items: cartItems.map(item => ({ item_id: item.id, quantity: item.quantity })),
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to create checkout session');
      console.error(error);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const categories = [...new Set(items.map(item => item.category))];

  return (
    <div className="min-h-screen bg-artsy-background">
      {/* Hero */}
      <section className="py-12 lg:py-16 bg-white border-b-2 border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Art <span className="text-teal-600">Materials</span> Shop
              </h1>
              <p className="font-body text-lg text-slate-600">
                Replacement materials and additional supplies for your young artist.
              </p>
            </div>
            <button
              onClick={() => setShowCart(true)}
              className="relative btn-secondary flex items-center gap-2"
              data-testid="open-cart"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden sm:inline">Cart</span>
              {cartItems.length > 0 && (
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 text-slate-900 text-xs font-bold rounded-full flex items-center justify-center border-2 border-slate-900">
                  {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            categories.map(category => (
              <div key={category} className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">{category}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {items.filter(item => item.category === category).map(item => (
                    <div key={item.id} className="card-sticker group" data-testid={`shop-item-${item.id}`}>
                      {item.is_starter_pack_item && (
                        <span className="tag-new absolute -top-2 -right-2">Starter Pack</span>
                      )}
                      <div className="aspect-square mb-4 overflow-hidden rounded-xl border-2 border-slate-200">
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <h3 className="font-heading font-bold text-slate-900 mb-1 line-clamp-1">{item.name}</h3>
                      <p className="font-body text-sm text-slate-600 mb-2 line-clamp-2">{item.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="font-heading text-xl font-bold text-teal-600">£{item.price.toFixed(2)}</span>
                        <button
                          onClick={() => handleAddToCart(item)}
                          className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center border-2 border-slate-900 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[1px] hover:translate-x-[1px] hover:shadow-none transition-all"
                          data-testid={`add-to-cart-${item.id}`}
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Cart Drawer */}
      {showCart && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCart(false)}></div>
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold text-slate-900">Your Cart</h2>
              <button onClick={() => setShowCart(false)} className="text-slate-500 hover:text-slate-700">
                <Trash2 className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {cartItems.length === 0 ? (
                <p className="font-body text-slate-600 text-center py-8">Your cart is empty</p>
              ) : (
                <div className="space-y-4">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      <img 
                        src={item.image_url} 
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-slate-900 text-sm">{item.name}</h3>
                        <p className="font-body text-teal-600 font-bold">£{item.price.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 bg-white rounded-full border border-slate-300 flex items-center justify-center"
                          data-testid={`decrease-${item.id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="font-body font-bold w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 bg-white rounded-full border border-slate-300 flex items-center justify-center"
                          data-testid={`increase-${item.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`remove-${item.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cartItems.length > 0 && (
              <div className="p-6 border-t border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-heading text-xl font-bold text-slate-900">Total</span>
                  <span className="font-heading text-2xl font-bold text-teal-600">£{total.toFixed(2)}</span>
                </div>
                <button
                  onClick={handleCheckout}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  disabled={checkoutLoading}
                  data-testid="checkout-btn"
                >
                  {checkoutLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      Checkout <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
                <button
                  onClick={() => { clearCart(); setShowCart(false); }}
                  className="btn-outline w-full mt-2"
                  data-testid="clear-cart"
                >
                  Clear Cart
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
